import _ from 'underscore';

import { Messages } from '../../codegen/Localize';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { IOAuthCredential, IOAuthCredentialTypeInfo } from '../../ts/ServerApi/types/IOAuthCredential';

// Use these strings to display info about a particular OAuth provider
export interface IOAuthCredentialDisplayStrings {
  displayName: string;
  promptEmbedCredential: string;
  noCredential: string;
  describeEmbedCredential: string;
  describeNoCredential: string;
}

// Base interface for getting information about what OAuth providers are available on the server
// as well as the behavior of the individual providers


// Implementation of IOAuthCredentialsService which gets its information from the sessionManagementService
export namespace OAuthCredentialsService  {

  export function getTypeInfo(typeStringKey: string): IOAuthCredentialTypeInfo {
    return _.find(this.getAllOAuthCredentialTypes(), info => info.type === typeStringKey
      || info.type === getStorageProviderFromCloudFileClassName(typeStringKey));
  }

  export function getAllOAuthCredentialTypes(): Array<IOAuthCredentialTypeInfo> {
    return getSessionManagementServiceInstance().oauthSettings() || [];
  }

  export function canAddForConnectionType(typeStringKey: string): boolean {
    return true; // anything we get back from the server is enabled
  }

  export function supportsGenericAuth(typeStringKey: string): boolean {
    const info = this.getTypeInfo(typeStringKey);
    return info ? info.supportsGenericAuth : false;
  }

  export function supportsTestConnection(typeStringKey: string, credential?: IOAuthCredential): boolean {
    return !this.supportsGenericAuth(typeStringKey);
  }

  export function getDisplayStrings(typeStringKey: string): IOAuthCredentialDisplayStrings {
    // Just return the generic description for everything except the provider's name
    const info = this.getTypeInfo(typeStringKey);
    return info && {
        displayName: getDisplayNameFromInfo(info),
        promptEmbedCredential : Messages.editDataConnections_generic_embedCredential(),
        noCredential: Messages.editDataConnections_generic_noCredential(),
        describeEmbedCredential: Messages.editDataConnections_generic_describeEmbedCredential(),
        describeNoCredential: Messages.editDataConnections_generic_describeNoCredential()
    };
  }

  export function getDisplayNameFromInfo(info: IOAuthCredentialTypeInfo): string {
     if (!info || !info.displayNames) {
       return '';
     }

     // info.displayNames will contain a mapping of locale to provider localized name.
     let locale = getSessionManagementServiceInstance().sessionLocale();
     if (!info.displayNames.hasOwnProperty(locale)) {
       // Just set it to en_US if we don't have the language present
       locale = 'en_US';
     }

     return info.displayNames[locale];
  }

  // For flow keychain, strip provider name from class name if it's a cloud file connection.
  // This is a copy of the native code logic:
  // teams_near/modules/connectors/tabconnect/main/infrastructure/CloudFileConnectorsRegistry.cpp
  function getStorageProviderFromCloudFileClassName(dbClass: string): string {
    if (dbClass.startsWith('cloudfile')) {
      // A full cloudfile class name is like this: `cloudfile:dropbox-textscan`
      const provider = dbClass.split(':')[1].split('-')[0];
      return provider;
    }
    return dbClass;
  }

  export function getDisplayNameFromType(connectionType: string): string {
    return getDisplayNameFromInfo(OAuthCredentialsService.getTypeInfo(connectionType));
  }
}

export type OAuthCredentialsService = typeof OAuthCredentialsService;